import PropTypes from 'prop-types';
import { useState } from 'react';
// @mui
import { useTheme } from '@mui/material/styles';
import { Box, Stack, Tooltip, IconButton, Typography } from '@mui/material';
// components
import Iconify from '../iconify';
import MenuPopover from '../menu-popover';
import { CustomAvatar } from '../custom-avatar';

// ----------------------------------------------------------------------

const ROLE_LABEL = 'Relationship Manager';

// Marks the contact-actions row so the sidebar card can keep it hidden until hovered.
const ACTIONS_CLASS = 'relationship-manager-actions';

const REVEALED_ACTIONS = { mt: 0.5, maxHeight: 40, opacity: 1 };

const digitsOf = (value) => String(value || '').replace(/\D/g, '');

/**
 * tel:, wa.me and mailto: links for a manager, or null for each channel the
 * manager has no data for. Phone numbers are sometimes stored already
 * prefixed with the country code, so the prefix is only added when missing.
 */
export function getManagerContactLinks(manager) {
  const countryCode = digitsOf(manager?.contact?.country_code);
  const phone = digitsOf(manager?.contact?.phone);
  const alreadyPrefixed =
    Boolean(countryCode) && phone.startsWith(countryCode) && phone.length > 10;
  const fullNumber = phone ? `${alreadyPrefixed ? '' : countryCode}${phone}` : '';
  const email = (manager?.contact?.email || '').trim();

  return {
    tel: fullNumber ? `tel:+${fullNumber}` : null,
    whatsapp: fullNumber ? `https://wa.me/${fullNumber}` : null,
    mailto: email ? `mailto:${email}` : null,
  };
}

// ----------------------------------------------------------------------

ContactActions.propTypes = {
  links: PropTypes.object,
};

function ContactActions({ links }) {
  const actions = [
    { key: 'tel', title: 'Call', icon: 'eva:phone-fill', href: links.tel },
    {
      key: 'whatsapp',
      title: 'WhatsApp',
      icon: 'tabler:brand-whatsapp',
      href: links.whatsapp,
      external: true,
    },
    { key: 'mailto', title: 'Email', icon: 'eva:email-fill', href: links.mailto },
  ].filter((action) => action.href);

  if (!actions.length) return null;

  return (
    // The negative margin cancels the buttons' padding so the first icon lines
    // up with the left edge of the name above it.
    <Stack direction="row" spacing={0.5} className={ACTIONS_CLASS} sx={{ mt: 0.5, ml: -0.5 }}>
      {actions.map((action) => (
        <Tooltip key={action.key} title={action.title}>
          <IconButton
            size="small"
            component="a"
            href={action.href}
            target={action.external ? '_blank' : undefined}
            rel={action.external ? 'noopener noreferrer' : undefined}
            aria-label={action.title}
            sx={{ p: 0.5, color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
          >
            <Iconify icon={action.icon} width={18} />
          </IconButton>
        </Tooltip>
      ))}
    </Stack>
  );
}

// ----------------------------------------------------------------------

ManagerSummary.propTypes = {
  manager: PropTypes.object,
  links: PropTypes.object,
};

// Photo on the left; name, the fixed role label and the contact icons stacked on the right.
function ManagerSummary({ manager, links }) {
  const name = manager?.full_name || '';
  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <CustomAvatar
        src={manager?.avatarUrl}
        alt={name}
        name={name}
        sx={{ width: 40, height: 40, flexShrink: 0 }}
      />
      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        <Typography variant="subtitle2" noWrap>
          {name}
        </Typography>
        <Typography variant="caption" noWrap sx={{ color: 'text.secondary', display: 'block' }}>
          {ROLE_LABEL}
        </Typography>
        <ContactActions links={links} />
      </Box>
    </Stack>
  );
}

// ----------------------------------------------------------------------

RelationshipManagerCard.propTypes = {
  manager: PropTypes.shape({
    full_name: PropTypes.string,
    avatarUrl: PropTypes.string,
    contact: PropTypes.object,
  }),
  compact: PropTypes.bool,
  sx: PropTypes.object,
};

/**
 * Shows a partner's relationship manager: photo, name, the label
 * "Relationship Manager", and call / WhatsApp / email icons that appear when
 * the card is hovered or focused (always visible on touch screens). `compact`
 * renders just the avatar, for the mini sidebar, and opens the details in a popover.
 */
export default function RelationshipManagerCard({ manager, compact = false, sx }) {
  const theme = useTheme();

  const [openPopover, setOpenPopover] = useState(null);

  if (!manager) return null;

  const name = manager.full_name || '';
  const links = getManagerContactLinks(manager);

  if (compact) {
    return (
      <>
        <Tooltip title={`${ROLE_LABEL}: ${name}`} placement="right">
          <IconButton
            aria-label={ROLE_LABEL}
            onClick={(event) => setOpenPopover(event.currentTarget)}
            sx={{ p: 0.5, mt: 1, mx: 'auto', flexShrink: 0, ...sx }}
          >
            <CustomAvatar
              src={manager.avatarUrl}
              alt={name}
              name={name}
              sx={{ width: 36, height: 36 }}
            />
          </IconButton>
        </Tooltip>

        {/* The popover is opened on purpose, so its icons are always shown. */}
        <MenuPopover
          open={openPopover}
          onClose={() => setOpenPopover(null)}
          arrow="left-top"
          sx={{ width: 240, p: 1.5 }}
        >
          <ManagerSummary manager={manager} links={links} />
        </MenuPopover>
      </>
    );
  }

  return (
    <Box sx={{ px: 2.5, pt: 1.5, pb: 2, flexShrink: 0, ...sx }}>
      <Box
        sx={{
          p: 1.5,
          borderRadius: 1.5,
          bgcolor: 'background.neutral',
          [`& .${ACTIONS_CLASS}`]: {
            mt: 0,
            maxHeight: 0,
            opacity: 0,
            overflow: 'hidden',
            transition: theme.transitions.create(['opacity', 'max-height', 'margin-top'], {
              duration: theme.transitions.duration.shorter,
            }),
          },
          // Keyboard users tabbing onto a hidden icon reveal the row too.
          [`&:hover .${ACTIONS_CLASS}, &:focus-within .${ACTIONS_CLASS}`]: REVEALED_ACTIONS,
          // Touch screens cannot hover, so the icons stay visible there.
          '@media (hover: none)': {
            [`& .${ACTIONS_CLASS}`]: REVEALED_ACTIONS,
          },
        }}
      >
        <ManagerSummary manager={manager} links={links} />
      </Box>
    </Box>
  );
}
